import { BorderStyle, EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  CornerRadius,
  FoundationColorTokens,
  Spacing,
  Typography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { Instance } from "mobx-state-tree";
import React, { ReactElement } from "react";
import { observer } from "mobx-react";
import { Stack } from "@mui/material";
import { GridSelectedGraph } from "./GridSelectedGraph";
import {
  DataProps,
  DataPropsForGridStackedAndGroupedBar,
  DataSection,
} from "../DataSection";
import { HeaderQuestionDetails } from "../HeaderQuestionDetails";
import { SurveyQuestionViewStoreNames } from "../../stores/SurveyViewDetailsStore";
import { GridSingleSelectGraphTypeV1 } from "@pulse/pulse-rpcs/lib/survey-dashboard/gridSingleSelectGraphTypeV1";
import { GridSingleSelectModel } from "../../models/GridSingleSelectModel";
import { ChartSelection, QuestionTypeGraphs } from "../ChartSelection";
import { SignInStore } from "../../../auth/store/SignInStore";
import { UserPrivileges } from "../../../store/user/UserPrivileges";

interface GridSingleSelectQuestionViewProps {
  typography: Typography;
  t: TFunction;
  tokens: FoundationColorTokens<string>;
  spacing: Spacing;
  border: BorderStyle;
  cornerRadius: CornerRadius;
  singleSelectQuestionView: Instance<typeof GridSingleSelectModel>;
  isChildQuestion: boolean;
  isSurveyViewOptionDisplayTextSelected: boolean;
  allowSPoCToSelectGraphType: boolean;
  signInStore: Instance<typeof SignInStore>;
}

export const GridSingleSelectQuestionView = observer(
  ({
    spacing,
    t,
    tokens,
    typography,
    border,
    cornerRadius,
    singleSelectQuestionView,
    isChildQuestion,
    isSurveyViewOptionDisplayTextSelected,
    allowSPoCToSelectGraphType,
    signInStore,
  }: GridSingleSelectQuestionViewProps): ReactElement => {
    const gridSingleSelectOptions: Set<string> = new Set();
    const gridSingleSelectRowOptions: Set<string> = new Set();
    const dataForGridBar =
      singleSelectQuestionView.gridSingleSelectRowResults.map(
        (gridSingleSelectRowResult): DataPropsForGridStackedAndGroupedBar => {
          gridSingleSelectRowOptions.add(
            gridSingleSelectRowResult.option ?? EMPTY_CHARACTER,
          );
          return {
            option: gridSingleSelectRowResult.option,
            answerCounts: gridSingleSelectRowResult.singleSelectResults.map(
              (singleSelectData) => {
                gridSingleSelectOptions.add(
                  isSurveyViewOptionDisplayTextSelected
                    ? (singleSelectData.answer.answer ?? EMPTY_CHARACTER)
                    : singleSelectData.answer.code,
                );
                return {
                  count: singleSelectData.count,
                  answer: isSurveyViewOptionDisplayTextSelected
                    ? (singleSelectData.answer.answer ?? EMPTY_CHARACTER)
                    : singleSelectData.answer.code,
                };
              },
            ),
            id: gridSingleSelectRowResult.code,
            rowOptionText: isSurveyViewOptionDisplayTextSelected
              ? (gridSingleSelectRowResult.option ?? EMPTY_CHARACTER)
              : gridSingleSelectRowResult.code,
            total: gridSingleSelectRowResult.totalResponses,
          };
        },
      );
    const getData = ():
      | DataProps[][]
      | DataPropsForGridStackedAndGroupedBar[] => {
      switch (singleSelectQuestionView.updatedGraphType) {
        case GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1.NONE:
        case GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1
          .PROPORTION: {
          return singleSelectQuestionView.gridSingleSelectRowResults.map(
            (gridSingleSelectRowResult): DataProps[] => {
              return gridSingleSelectRowResult.singleSelectResults.map(
                (singleSelectResult): DataProps => {
                  return {
                    option: isSurveyViewOptionDisplayTextSelected
                      ? singleSelectResult.answer.answer
                      : singleSelectResult.answer.code,
                    count: singleSelectResult.count,
                    percentage: singleSelectResult.percentage,
                    id: singleSelectResult.answer.code,
                  };
                },
              );
            },
          );
        }
        case GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1
          .GROUPED_BAR:
        case GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1
          .STACKED_COLUMN: {
          return dataForGridBar;
        }
      }
    };

    return (
      <Stack
        width="100%"
        alignItems="center"
        borderRadius={cornerRadius.radiusXS}
        gap={spacing.spaceXL}
        paddingBottom={spacing.space2XL}
      >
        <Stack
          padding={spacing.spaceSM}
          width="100%"
          borderBottom={border.default}
        >
          <Stack direction="row">
            <HeaderQuestionDetails
              questionText={singleSelectQuestionView.columnHeader}
              sampleBreakdown={undefined}
              typography={typography}
              tokens={tokens}
              t={t}
              spacing={spacing}
              surveyQuestionViewType={
                SurveyQuestionViewStoreNames.GRID_SINGLE_SELECT_MODEL
              }
              isQuestionTypeIconVisible={false}
            />
            {(allowSPoCToSelectGraphType ||
              signInStore.isPrivilegeGranted(
                UserPrivileges.processSurveyDashboardViews,
              )) && (
              <ChartSelection
                border={border}
                cornerRadius={cornerRadius}
                spacing={spacing}
                surveyQuestionViewType={
                  SurveyQuestionViewStoreNames.GRID_SINGLE_SELECT_MODEL
                }
                selectedGraph={
                  QuestionTypeGraphs[singleSelectQuestionView.updatedGraphType]
                }
                onChartIconClick={
                  singleSelectQuestionView.updateSelectedGraphType
                }
                tokens={tokens}
              />
            )}
          </Stack>
        </Stack>
        <Stack padding={spacing.space2XL} gap={spacing.spaceXL} width="100%">
          <GridSelectedGraph
            selectedGraph={
              QuestionTypeGraphs[singleSelectQuestionView.updatedGraphType]
            }
            data={getData()}
            isNeutral={true}
            cornerRadius={cornerRadius}
            tokens={tokens}
            spacing={spacing}
            typography={typography}
            border={border}
            gridSingleSelectRowOptions={[...gridSingleSelectRowOptions]}
            t={t}
            isChildQuestion={isChildQuestion}
            isSurveyViewOptionDisplayTextSelected={
              isSurveyViewOptionDisplayTextSelected
            }
          />
          <DataSection
            spacing={spacing}
            t={t}
            typography={typography}
            tokens={tokens}
            cornerRadius={cornerRadius}
            total={null}
            data={dataForGridBar}
            border={border}
            isColoredSwatchRequired={false}
            customOptionHeaders={[...gridSingleSelectOptions]}
            isSurveyViewOptionDisplayTextSelected={
              isSurveyViewOptionDisplayTextSelected
            }
            isGridTotalPresent={true}
          />
        </Stack>
      </Stack>
    );
  },
);
